// =====================================================================================
// INVRT Radar — web console — consent banner designer — form model
//
// The designer's form state, the rules it is checked against, and the preview document.
// Pure: no React, no fetch. Everything here MIRRORS supabase/functions/_shared/
// banner_config.ts (the zod schema every PUT /sites/:id/banner goes through): the same
// enumerations, the same length caps, the same https-only rule, the same defaults. The
// server is still the authority; the mirror exists so a tenant learns about a refused
// field before the round trip, not after.
//
// Numbers are held as STRINGS in the form so a field can be emptied while typing without
// the state lying about it; configFromForm() turns them back into the wire shape.
// =====================================================================================

import type {
  BannerConfig,
  BannerLanguage,
  BannerPosition,
} from "@/lib/types.c1";

export const BANNER_POSITIONS: readonly BannerPosition[] = [
  "bottom",
  "top",
  "bottom-left",
  "bottom-right",
  "centre",
];

export const POSITION_LABELS: Record<BannerPosition, string> = {
  bottom: "Bottom bar",
  top: "Top bar",
  "bottom-left": "Bottom left card",
  "bottom-right": "Bottom right card",
  centre: "Centre",
};

export const BANNER_LANGUAGES: readonly BannerLanguage[] = [
  "en-GB",
  "en-US",
  "en-AU",
  "de",
  "fr",
  "es",
  "nl",
  "it",
  "pt",
];

export const LANGUAGE_LABELS: Record<BannerLanguage, string> = {
  "en-GB": "English (UK)",
  "en-US": "English (US)",
  "en-AU": "English (Australia)",
  de: "Deutsch",
  fr: "Français",
  es: "Español",
  nl: "Nederlands",
  it: "Italiano",
  pt: "Português",
};

/** The server's caps (banner_config.ts), in one place. */
export const BANNER_LIMITS = {
  title: 80,
  body: 600,
  accept: 30,
  reject: 30,
  manage: 30,
  logo_url: 512,
  url: 2048,
  radius: { min: 0, max: 24 },
  reconsent_days: { min: 30, max: 730 },
} as const;

/**
 * What a never-published site shows (banner_config.ts's DEFAULT_BANNER_CONFIG, verbatim).
 * The privacy policy URL is empty on purpose: Radar never invents a tenant's policy
 * address, and validateBannerForm() refuses to publish until one is supplied.
 */
export const DEFAULT_BANNER_CONFIG: BannerConfig = Object.freeze({
  position: "bottom",
  theme: {
    background: "#ffffff",
    text: "#0f172a",
    accent: "#405bd8",
    accent_text: "#ffffff",
    border_radius_px: 12,
  },
  copy: {
    title: "We use cookies",
    body:
      "We use cookies to understand which companies visit this site and to improve it. " +
      "You can accept or reject cookies that are not strictly necessary.",
    accept: "Accept",
    reject: "Reject",
    manage: "Privacy policy",
  },
  links: { privacy_policy_url: "" },
  language: "en-GB",
  behaviour: { reject_button: true, reconsent_days: 180, block_until_choice: true },
}) as BannerConfig;

export interface BannerFormState {
  position: BannerPosition;
  theme: {
    background: string;
    text: string;
    accent: string;
    accent_text: string;
    border_radius_px: string;
  };
  logo_url: string;
  copy: { title: string; body: string; accept: string; reject: string; manage: string };
  links: { privacy_policy_url: string; cookie_policy_url: string };
  language: BannerLanguage;
  behaviour: { reject_button: boolean; reconsent_days: string; block_until_choice: boolean };
}

/** Dotted paths, one per field the designer renders. */
export type BannerFieldKey =
  | "position"
  | "theme.background"
  | "theme.text"
  | "theme.accent"
  | "theme.accent_text"
  | "theme.border_radius_px"
  | "logo_url"
  | "copy.title"
  | "copy.body"
  | "copy.accept"
  | "copy.reject"
  | "copy.manage"
  | "links.privacy_policy_url"
  | "links.cookie_policy_url"
  | "language"
  | "behaviour.reject_button"
  | "behaviour.reconsent_days"
  | "behaviour.block_until_choice";

export type BannerFormErrors = Partial<Record<BannerFieldKey, string>>;

export const HEX_COLOUR_MESSAGE = "Use a six-digit hex colour such as #405bd8.";
export const PRIVACY_URL_REQUIRED_MESSAGE = "Enter the https:// address of your privacy policy.";

const HEX_COLOUR = /^#[0-9a-fA-F]{6}$/;

export function isHexColour(value: string): boolean {
  return HEX_COLOUR.test(value);
}

export function formFromConfig(config: BannerConfig): BannerFormState {
  return {
    position: config.position,
    theme: {
      background: config.theme.background,
      text: config.theme.text,
      accent: config.theme.accent,
      accent_text: config.theme.accent_text,
      border_radius_px: String(config.theme.border_radius_px),
    },
    logo_url: config.logo_url ?? "",
    copy: {
      title: config.copy.title,
      body: config.copy.body,
      accept: config.copy.accept,
      reject: config.copy.reject,
      manage: config.copy.manage ?? "",
    },
    links: {
      privacy_policy_url: config.links.privacy_policy_url,
      cookie_policy_url: config.links.cookie_policy_url ?? "",
    },
    language: config.language,
    behaviour: {
      reject_button: config.behaviour.reject_button,
      reconsent_days: String(config.behaviour.reconsent_days),
      block_until_choice: config.behaviour.block_until_choice,
    },
  };
}

function integerOr(value: string, fallback: number): number {
  const parsed = Number.parseInt(value.trim(), 10);
  return Number.isFinite(parsed) ? parsed : fallback;
}

/**
 * The wire shape (and the preview's input). Optional leaves are OMITTED when blank — the
 * server's schema is `.strict()` and its optionals are "absent", never "empty string".
 * Colours are lower-cased so two publishes of the same colour compare equal, as the
 * server normalises them. Lenient about numbers (a half-typed field falls back to the
 * default) so the preview always has something to render; validateBannerForm() is the
 * gate before a publish.
 */
export function configFromForm(form: BannerFormState): BannerConfig {
  const logoUrl = form.logo_url.trim();
  const cookiePolicyUrl = form.links.cookie_policy_url.trim();
  const manage = form.copy.manage.trim();
  return {
    position: form.position,
    theme: {
      background: form.theme.background.trim().toLowerCase(),
      text: form.theme.text.trim().toLowerCase(),
      accent: form.theme.accent.trim().toLowerCase(),
      accent_text: form.theme.accent_text.trim().toLowerCase(),
      border_radius_px: integerOr(
        form.theme.border_radius_px,
        DEFAULT_BANNER_CONFIG.theme.border_radius_px,
      ),
    },
    ...(logoUrl === "" ? {} : { logo_url: logoUrl }),
    copy: {
      title: form.copy.title.trim(),
      body: form.copy.body.trim(),
      accept: form.copy.accept.trim(),
      reject: form.copy.reject.trim(),
      ...(manage === "" ? {} : { manage }),
    },
    links: {
      privacy_policy_url: form.links.privacy_policy_url.trim(),
      ...(cookiePolicyUrl === "" ? {} : { cookie_policy_url: cookiePolicyUrl }),
    },
    language: form.language,
    behaviour: {
      reject_button: form.behaviour.reject_button,
      reconsent_days: integerOr(
        form.behaviour.reconsent_days,
        DEFAULT_BANNER_CONFIG.behaviour.reconsent_days,
      ),
      block_until_choice: form.behaviour.block_until_choice,
    },
  };
}

function httpsUrlError(value: string, maxLength: number): string | null {
  if (value.length > maxLength) return `${maxLength} characters or fewer.`;
  let parsed: URL;
  try {
    parsed = new URL(value);
  } catch {
    return "Must be a valid URL.";
  }
  if (parsed.protocol !== "https:") return "Must be an https:// URL.";
  return null;
}

function lengthError(value: string, max: number): string | null {
  if (value.length === 0) return "Required.";
  if (value.length > max) return `${max} characters or fewer.`;
  return null;
}

function integerRangeError(value: string, min: number, max: number, unit = ""): string | null {
  const trimmed = value.trim();
  if (trimmed === "" || !/^-?\d+$/.test(trimmed)) return "Enter a whole number.";
  const parsed = Number.parseInt(trimmed, 10);
  if (parsed < min || parsed > max) return `Between ${min} and ${max}${unit}.`;
  return null;
}

/** Every rule banner_config.ts enforces, keyed by field. An empty object means "publishable". */
export function validateBannerForm(form: BannerFormState): BannerFormErrors {
  const errors: BannerFormErrors = {};
  const set = (key: BannerFieldKey, message: string | null): void => {
    if (message !== null) errors[key] = message;
  };

  if (!BANNER_POSITIONS.includes(form.position)) set("position", "Choose a position.");
  if (!BANNER_LANGUAGES.includes(form.language)) set("language", "Choose a language.");

  for (const key of ["background", "text", "accent", "accent_text"] as const) {
    if (!isHexColour(form.theme[key].trim())) set(`theme.${key}`, HEX_COLOUR_MESSAGE);
  }
  set(
    "theme.border_radius_px",
    integerRangeError(
      form.theme.border_radius_px,
      BANNER_LIMITS.radius.min,
      BANNER_LIMITS.radius.max,
    ),
  );

  const logoUrl = form.logo_url.trim();
  if (logoUrl !== "") set("logo_url", httpsUrlError(logoUrl, BANNER_LIMITS.logo_url));

  set("copy.title", lengthError(form.copy.title.trim(), BANNER_LIMITS.title));
  set("copy.body", lengthError(form.copy.body.trim(), BANNER_LIMITS.body));
  set("copy.accept", lengthError(form.copy.accept.trim(), BANNER_LIMITS.accept));
  set("copy.reject", lengthError(form.copy.reject.trim(), BANNER_LIMITS.reject));
  const manage = form.copy.manage.trim();
  if (manage.length > BANNER_LIMITS.manage) {
    set("copy.manage", `${BANNER_LIMITS.manage} characters or fewer.`);
  }

  const privacyUrl = form.links.privacy_policy_url.trim();
  if (privacyUrl === "") set("links.privacy_policy_url", PRIVACY_URL_REQUIRED_MESSAGE);
  else set("links.privacy_policy_url", httpsUrlError(privacyUrl, BANNER_LIMITS.url));
  const cookieUrl = form.links.cookie_policy_url.trim();
  if (cookieUrl !== "") set("links.cookie_policy_url", httpsUrlError(cookieUrl, BANNER_LIMITS.url));

  set(
    "behaviour.reconsent_days",
    integerRangeError(
      form.behaviour.reconsent_days,
      BANNER_LIMITS.reconsent_days.min,
      BANNER_LIMITS.reconsent_days.max,
      " days",
    ),
  );

  return errors;
}

/**
 * JSON that is safe to place inside a <script> element of any type: every `<`, `>` and `&`
 * becomes its \u escape (so "</script>" and "<!--" can never end the element early) and
 * the two line terminators JSON allows but JavaScript source does not are escaped as well.
 * JSON.parse reads the escapes back to the original characters.
 */
export function serialiseForScript(value: unknown): string {
  return JSON.stringify(value)
    .replace(/</g, "\\u003c")
    .replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026")
    .replace(/\u2028/g, "\\u2028")
    .replace(/\u2029/g, "\\u2029");
}

/** The id of the JSON data block the preview bootstrap reads (previewBoot.js, CONFIG_ID). */
export const PREVIEW_CONFIG_BLOCK_ID = "radar-banner-preview-config";

/** The loader, served by this console from its own origin (web/public/r.js). */
export const PREVIEW_LOADER_SRC = "/r.js";

/**
 * The preview bootstrap, served by this console from its own origin as a plain static file
 * (web/public/previewBoot.js — never bundled, never inlined as a data: URL). Same-origin
 * matters: the console's Content-Security-Policy (web/public/_headers) is `script-src 'self'`
 * and srcdoc documents inherit it, so an inline bootstrap could never run.
 */
export const PREVIEW_BOOT_SRC = "/previewBoot.js";

/**
 * The preview page: a plain stand-in for the tenant's site, the current configuration in a
 * JSON data block (type="application/json" is data to the browser, never a script), the
 * bootstrap that publishes it as window.__RADAR_BANNER_PREVIEW__, then the REAL loader. In
 * preview mode the loader renders at once and never fetches, stores or injects a pixel. The
 * iframe that hosts this page is sandboxed to scripts only, so the document has an opaque
 * origin and no reach into the console.
 *
 * Nothing inline, on purpose: the console's CSP (script-src 'self'; style-src 'self') is
 * inherited by this document, so the page carries no inline script and no <style>; the
 * bootstrap styles the stand-in text through the CSSOM and re-applies the loader's own
 * stylesheet the same way (see previewBoot.js).
 */
export function buildPreviewSrcdoc(config: BannerConfig): string {
  return [
    "<!doctype html>",
    '<html lang="en">',
    "<head>",
    '<meta charset="utf-8">',
    '<meta name="viewport" content="width=device-width,initial-scale=1">',
    "<title>Banner preview</title>",
    "</head>",
    "<body>",
    "<main>",
    "<h1>Your website</h1>",
    "<p>This page stands in for your own. The banner below is rendered by the same script your visitors will load.</p>",
    "<p>Change a setting on the left and the preview follows. Nothing is published until you choose Publish banner.</p>",
    "<p>Accept, reject and the policy links work here exactly as they will on your pages, but no choice is recorded.</p>",
    "</main>",
    `<script type="application/json" id="${PREVIEW_CONFIG_BLOCK_ID}">${serialiseForScript(config)}</script>`,
    `<script src="${PREVIEW_BOOT_SRC}"></script>`,
    `<script src="${PREVIEW_LOADER_SRC}"></script>`,
    "</body>",
    "</html>",
  ].join("\n");
}
